// -----------------------------------------------------------------------------
// preflight/detect.ts — the capability probe.
//
// Runs once, on entry, before any editor code loads. It asks the browser what it
// can actually do (decode, encode, WebGL2, Web Audio, storage, RAM) and returns
// a single Capabilities object. main.ts uses the `tier` field to route the user
// into the editor (full / degraded) or the "blocked" screen, and the rest of the
// object drives runtime config (export path, proxy resolution, OPFS vs IDB).
//
// This file is reproduced verbatim from CLAUDE.md §5 — it is the contract.
// -----------------------------------------------------------------------------
export type Tier = 'full' | 'degraded' | 'blocked';

export interface Capabilities {
  tier: Tier;
  webgl2: boolean; webgpu: boolean; webAudio: boolean;
  videoDecode: boolean; videoEncodeHW: boolean; videoEncodeSW: boolean;
  audioEncodeAAC: boolean; audioEncodeOpus: boolean;
  opfs: boolean; fsAccess: boolean; crossOriginIsolated: boolean;
  deviceMemoryGB: number | null; cores: number | null; storageQuotaGB: number | null;
  reasons: string[];
}

const H264 = 'avc1.42E01E';   // baseline, 1080p-safe
const AAC  = 'mp4a.40.2';

async function canDecodeVideo(): Promise<boolean> {
  if (!('VideoDecoder' in window)) return false;
  try {
    const s = await VideoDecoder.isConfigSupported({ codec: H264, codedWidth: 1920, codedHeight: 1080 });
    return !!s.supported;
  } catch { return false; }
}
async function canEncodeVideo(pref: 'prefer-hardware' | 'prefer-software'): Promise<boolean> {
  if (!('VideoEncoder' in window)) return false;
  try {
    const s = await VideoEncoder.isConfigSupported({
      codec: H264, width: 1920, height: 1080, bitrate: 8_000_000, framerate: 30,
      hardwareAcceleration: pref,
    });
    return !!s.supported;
  } catch { return false; }
}
async function canEncodeAudio(codec: string): Promise<boolean> {
  if (!('AudioEncoder' in window)) return false;
  try {
    const s = await AudioEncoder.isConfigSupported({ codec, sampleRate: 48000, numberOfChannels: 2, bitrate: 128_000 });
    return !!s.supported;
  } catch { return false; }
}
function hasWebGL2(): boolean {
  try { return !!document.createElement('canvas').getContext('webgl2'); } catch { return false; }
}
async function hasWebGPU(): Promise<boolean> {
  if (!('gpu' in navigator)) return false;
  try { return !!(await (navigator as any).gpu.requestAdapter()); } catch { return false; }
}

export async function detectCapabilities(): Promise<Capabilities> {
  const reasons: string[] = [];
  const webgl2 = hasWebGL2();
  const webgpu = await hasWebGPU();
  const webAudio = 'AudioContext' in window || 'webkitAudioContext' in window;
  const videoDecode = await canDecodeVideo();
  const videoEncodeHW = await canEncodeVideo('prefer-hardware');
  const videoEncodeSW = videoEncodeHW || await canEncodeVideo('prefer-software');
  const audioEncodeAAC = await canEncodeAudio(AAC);
  const audioEncodeOpus = await canEncodeAudio('opus');
  const opfs = !!(navigator.storage && 'getDirectory' in navigator.storage);
  const fsAccess = 'showOpenFilePicker' in window;
  const coi = (self as any).crossOriginIsolated === true;
  const deviceMemoryGB = (navigator as any).deviceMemory ?? null;
  const cores = navigator.hardwareConcurrency ?? null;

  let storageQuotaGB: number | null = null;
  try { const est = await navigator.storage?.estimate?.(); if (est?.quota) storageQuotaGB = +(est.quota / 1e9).toFixed(1); } catch {}

  let tier: Tier = 'full';
  if (!webgl2)      { reasons.push('No WebGL2 — preview compositor cannot run.'); tier = 'blocked'; }
  if (!videoDecode) { reasons.push('No WebCodecs H.264 decode — media cannot play.'); tier = 'blocked'; }
  if (!webAudio)    { reasons.push('No Web Audio — audio cannot be processed.'); tier = 'blocked'; }
  if (tier !== 'blocked') {
    if (!videoEncodeSW)      { reasons.push('No native H.264 encode — export uses FFmpeg.wasm (slow).'); tier = 'degraded'; }
    else if (!videoEncodeHW) { reasons.push('Software-only encode — export will be slower.'); tier = 'degraded'; }
    if (!audioEncodeAAC && !audioEncodeOpus) { reasons.push('No native audio encode — audio export via FFmpeg.wasm.'); tier = 'degraded'; }
    if ((deviceMemoryGB ?? 8) < 4) { reasons.push('Low device memory — preview will use a lower-resolution proxy.'); tier = 'degraded'; }
  }
  return { tier, webgl2, webgpu, webAudio, videoDecode, videoEncodeHW, videoEncodeSW,
           audioEncodeAAC, audioEncodeOpus, opfs, fsAccess, crossOriginIsolated: coi,
           deviceMemoryGB, cores, storageQuotaGB, reasons };
}
